import { useState } from "react";
import { useApp } from "../context/AppContext";
import { groupTabLines } from "@/lib/billing";
import AddToTabDialog from "./AddToTabDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Minus, UserPlus } from "lucide-react";
import { formatMoney } from "../lib/format";

const tabTotal = (tab) => tab.orders.reduce((sum, o) => sum + o.total_amount, 0);

const itemCount = (tab) => {
  const count = tab.orders.reduce((sum, o) => sum + o.quantity, 0);
  return `${count} item${count === 1 ? "" : "s"}`;
};

function formatTime(value) {
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Asked when Start Game is tapped while anyone waiting has bought something. The
 * app can't know who got this table, and a guess would put a bill on the wrong
 * people, so it asks once rather than leaving it to be remembered.
 */
export function WaitingPickDialog({ tabs, table, onPick, onSkip, onClose }) {
  return (
    <Dialog open={tabs.length > 0} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md" data-testid={`waiting-pick-${table.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Who's playing on {table.name}?</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Pick who was waiting, and what they bought goes on this table's bill.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`waiting-pick-tab-${tab.id}`}
              onClick={() => onPick(tab)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#09090b] border border-zinc-700 text-left hover:border-amber-500/60 transition-colors duration-150"
            >
              <span className="min-w-0">
                <span className="block font-medium text-zinc-100 break-words">{tab.name}</span>
                <span className="block text-xs text-zinc-500">{itemCount(tab)}</span>
              </span>
              <span className="shrink-0 font-semibold text-amber-400">₹{formatMoney(tabTotal(tab))}</span>
            </button>
          ))}
          <button
            type="button"
            data-testid={`waiting-pick-none-${table.id}`}
            onClick={onSkip}
            className="w-full px-4 py-3 rounded-lg border border-zinc-800 text-sm text-zinc-300 hover:bg-zinc-800/50 transition-colors duration-150"
          >
            Someone else
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WaitingCard({ tab, gamesRunning, onShop, onSeat, onClose }) {
  const { setWaitingQuantity } = useApp();
  const [removingId, setRemovingId] = useState(null);
  const lines = groupTabLines(tab.orders);
  const total = tabTotal(tab);
  const empty = tab.orders.length === 0;
  const actionClass = "flex-1 min-w-0 h-10 px-1 flex items-center justify-center whitespace-nowrap text-xs font-medium text-zinc-300 hover:bg-zinc-800/50 transition-colors duration-150";

  const handleRemoveOne = async (line) => {
    const last = line.entries[line.entries.length - 1];
    setRemovingId(line.itemId);
    try {
      await setWaitingQuantity(tab.id, last.id, last.quantity - 1);
      toast(`Removed one ${line.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the item");
    }
    setRemovingId(null);
  };

  return (
    <div className="bg-[#18181b] border border-amber-500/30 rounded-xl overflow-hidden" data-testid={`waiting-tab-${tab.id}`}>
      <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <p className="font-heading text-lg font-semibold text-zinc-100 break-words">{tab.name}</p>
          <p className="text-[11px] text-zinc-500">Waiting since {formatTime(tab.created_at)}</p>
        </div>
        {!empty && (
          <span className="shrink-0 font-heading text-lg font-bold text-amber-400" data-testid={`waiting-total-${tab.id}`}>
            ₹{formatMoney(total)}
          </span>
        )}
      </div>

      {lines.length > 0 && (
        <div className="max-h-32 overflow-y-auto divide-y divide-zinc-800/70 border-t border-zinc-800/70">
          {lines.map((line) => (
            <div key={line.itemId} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`waiting-line-${tab.id}-${line.itemId}`}>
              <button
                type="button"
                aria-label={`Remove one ${line.name}`}
                onClick={() => handleRemoveOne(line)}
                disabled={removingId === line.itemId}
                className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 transition-colors duration-150"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="flex-1 min-w-0 text-zinc-200 break-words">{line.name}</span>
              <span className="text-zinc-500 text-xs shrink-0">× {line.quantity}</span>
              <span className="text-zinc-100 font-medium shrink-0 w-14 text-right">₹{formatMoney(line.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex divide-x divide-zinc-800 border-t border-zinc-800">
        <button type="button" data-testid={`waiting-add-${tab.id}`} onClick={() => onShop(tab)} className={actionClass}>
          {empty ? "Add items" : "Add more"}
        </button>
        {!empty && gamesRunning && (
          <button type="button" data-testid={`waiting-seat-${tab.id}`} onClick={() => onSeat(tab)} className={actionClass}>
            On a table
          </button>
        )}
        <button type="button" data-testid={`waiting-close-${tab.id}`} onClick={() => onClose(tab)} className={actionClass}>
          {empty ? "Remove" : "Paid"}
        </button>
      </div>
    </div>
  );
}

/**
 * People buying while every table is taken. Their items stay on their name until
 * they get a table, then join that table's bill. A name that bought nothing just
 * sits on the list until removed.
 */
export default function WaitingTabs() {
  const { waiting, tables, addWaiting, addToWaiting, seatWaiting, closeWaiting } = useApp();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  // Held by id: orders change while these dialogs are open, so read the live tab.
  const [shoppingId, setShoppingId] = useState(null);
  const [seatingId, setSeatingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const shopping = waiting.find((t) => t.id === shoppingId);
  const seating = waiting.find((t) => t.id === seatingId);
  const closing = waiting.find((t) => t.id === closingId);
  const runningTables = tables.filter((t) => t.status === "occupied");

  const handleAdd = async (e) => {
    e.preventDefault();
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      const tab = await addWaiting(name.trim());
      setAdding(false);
      setName("");
      setShoppingId(tab.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add them");
    }
    setSaving(false);
  };

  const handleSeat = async (table) => {
    if (busy || !seating) return;
    setBusy(true);
    try {
      await seatWaiting(seating.id, table.id);
      toast.success(`${seating.name}'s items are on ${table.name}`);
      setSeatingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't put them on that table");
    }
    setBusy(false);
  };

  const handleClose = async () => {
    if (busy || !closing) return;
    setBusy(true);
    try {
      await closeWaiting(closing.id);
      toast.success(`${closing.name} is off the waiting list`);
      setClosingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update the waiting list");
    }
    setBusy(false);
  };

  const handleCloseTap = (tab) => {
    // Nothing owed, so there is nothing to confirm.
    if (tab.orders.length > 0) {
      setClosingId(tab.id);
      return;
    }
    closeWaiting(tab.id).catch((err) => {
      toast.error(err?.response?.data?.detail || "Couldn't update the waiting list");
    });
  };

  return (
    <section className="mb-8" data-testid="waiting-section">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold tracking-tight text-zinc-100">Waiting for a table</h2>
        <Button
          type="button"
          data-testid="waiting-new-btn"
          onClick={() => setAdding(true)}
          variant="outline"
          className="shrink-0 h-9 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-zinc-100"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Add someone
        </Button>
      </div>

      {waiting.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {waiting.map((tab) => (
            <WaitingCard
              key={tab.id}
              tab={tab}
              gamesRunning={runningTables.length > 0}
              onShop={(t) => setShoppingId(t.id)}
              onSeat={(t) => setSeatingId(t.id)}
              onClose={handleCloseTap}
            />
          ))}
        </div>
      )}

      <Dialog open={adding} onOpenChange={(open) => { if (!saving) setAdding(open); }}>
        <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-sm" data-testid="waiting-new-dialog">
          <form onSubmit={handleAdd}>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-tight">Who's waiting?</DialogTitle>
              <DialogDescription className="text-zinc-400">
                What they buy stays on this name until they get a table.
              </DialogDescription>
            </DialogHeader>
            <Input
              autoFocus
              data-testid="waiting-name-input"
              placeholder="Name"
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              className="my-4 h-11 bg-[#09090b] border-zinc-700 text-zinc-200 placeholder:text-zinc-500"
            />
            <DialogFooter>
              <Button
                type="submit"
                data-testid="waiting-name-save"
                disabled={saving || !name.trim()}
                className="w-full h-11 font-heading font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                {saving ? "Adding..." : "Next: add items"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {shopping && (
        <AddToTabDialog
          open
          onOpenChange={(open) => { if (!open) setShoppingId(null); }}
          table={{ id: shopping.id, name: shopping.name, active_session: { orders: shopping.orders } }}
          onAdd={(itemId, quantity) => addToWaiting(shopping.id, itemId, quantity)}
        />
      )}

      <Dialog open={!!seating} onOpenChange={(open) => { if (!open && !busy) setSeatingId(null); }}>
        <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md" data-testid="waiting-seat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Put {seating?.name} on which table?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              For a game already running. To start a new one, just tap Start Game on a free table.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {runningTables.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`waiting-seat-on-${t.id}`}
                disabled={busy}
                onClick={() => handleSeat(t)}
                className="w-full px-4 py-3 rounded-lg bg-[#09090b] border border-zinc-700 text-left font-medium text-zinc-100 hover:border-amber-500/60 disabled:opacity-60 transition-colors duration-150"
              >
                {t.name}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!closing} onOpenChange={(open) => { if (!open && !busy) setClosingId(null); }}>
        <AlertDialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md" data-testid="waiting-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl tracking-tight">
              {closing?.name} paid ₹{closing ? formatMoney(tabTotal(closing)) : 0}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              For someone who paid at the counter and isn't playing. The items stay
              recorded as cafe sales and the name comes off this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100">
              Not yet
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleClose(); }}
              disabled={busy}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="waiting-close-confirm"
            >
              Paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
